import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import './BaseConhecimento.css';
import AnexoPreviewModal from './AnexoPreviewModal';
import { useDataRefresher } from '../hooks/useDataRefresher';
import {
  FaPlus, FaSearch, FaBook, FaEdit, FaTrash, FaPaperclip, FaCheckCircle, FaTimesCircle,
  FaSave, FaPaperPlane, FaUndo, FaCopy, FaLock, FaThumbsUp, FaThumbsDown, FaFilter,
  FaVideo, FaFileWord, FaFilePdf, FaFileImage, FaCloudUploadAlt, FaFolderOpen, FaClock,
  FaUserShield, FaLayerGroup, FaStar, FaRegStar, FaCommentDots, FaHistory, FaChartLine,
  FaLink, FaImage, FaFire, FaTimes, FaTag, FaEye,
} from 'react-icons/fa';

const normalizePerfil = (perfil) => (perfil || '').toLowerCase().trim();
const formatBytes = (bytes = 0) =>
  !bytes ? '0 KB' : bytes / 1024 < 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1024 / 1024).toFixed(2)} MB`;
const formatDateTime = (value) => (value ? new Date(value).toLocaleString('pt-BR') : '—');
const safeArray = (value) => (Array.isArray(value) ? value : []);
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Copiado com sucesso.');
  } catch {
    toast.error('Não foi possível copiar.');
  }
};

const getTipoAnexo = (mimetype = '', nome = '') => {
  const lowerMime = (mimetype || '').toLowerCase();
  const lowerName = (nome || '').toLowerCase();
  if (lowerMime.startsWith('video/')) return 'video';
  if (lowerMime.startsWith('image/')) return 'imagem';
  if (lowerMime.includes('pdf') || lowerName.endsWith('.pdf')) return 'pdf';
  if (
    lowerMime.includes('word') ||
    lowerMime.includes('officedocument.wordprocessingml') ||
    lowerName.endsWith('.doc') ||
    lowerName.endsWith('.docx')
  ) return 'word';
  return 'arquivo';
};

const getAnexoIcon = (anexo) => {
  const tipo = getTipoAnexo(anexo?.mimetype, anexo?.nome_original);
  switch (tipo) {
    case 'video': return <FaVideo />;
    case 'imagem': return <FaFileImage />;
    case 'pdf': return <FaFilePdf />;
    case 'word': return <FaFileWord />;
    default: return <FaPaperclip />;
  }
};

const buildFallbackRelated = (artigo, artigos = []) => {
  if (!artigo) return [];
  const tagsAtuais = safeArray(artigo.tags).map((t) => String(t).toLowerCase());

  return artigos
    .filter((item) => item.id !== artigo.id && item.status === 'Aprovado')
    .map((item) => {
      const tagsItem = safeArray(item.tags).map((t) => String(t).toLowerCase());
      const categoriaScore = item.categoria === artigo.categoria ? 3 : 0;
      const tagScore = tagsItem.filter((tag) => tagsAtuais.includes(tag)).length * 2;
      const tituloScore = String(item.titulo || '')
        .toLowerCase()
        .split(' ')
        .some((word) => String(artigo.titulo || '').toLowerCase().includes(word))
        ? 1
        : 0;

      return { ...item, relatedScore: categoriaScore + tagScore + tituloScore };
    })
    .filter((item) => item.relatedScore > 0)
    .sort((a, b) => b.relatedScore - a.relatedScore)
    .slice(0, 5);
};

const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  return (
    <div className="editor-toolbar">
      <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''}>
        <strong>B</strong>
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''}>
        <em>I</em>
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive('strike') ? 'is-active' : ''}>
        <s>S</s>
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'is-active' : ''}>
        Lista
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive('orderedList') ? 'is-active' : ''}>
        Lista Num.
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={editor.isActive('blockquote') ? 'is-active' : ''}>
        Citação
      </button>
    </div>
  );
};

const SubmitModal = ({ onClose, onSubmeter }) => {
  const [aprovadores, setAprovadores] = useState([]);
  const [selectedAprovador, setSelectedAprovador] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/usuarios/aprovadores')
      .then(({ data }) => {
        setAprovadores(data || []);
        if (data?.length) setSelectedAprovador(data[0].id);
      })
      .catch(() => toast.error('Não foi possível carregar a lista de aprovadores.'))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = () => {
    if (!selectedAprovador) return toast.warn('Selecione um aprovador.');
    onSubmeter(selectedAprovador);
  };

  return (
    <div className="bc-modal-overlay">
      <div className="bc-modal-content bc-submit-modal">
        <button onClick={onClose} className="bc-modal-close-button">&times;</button>
        <h3>Submeter para Aprovação</h3>
        <p>Selecione o responsável que irá revisar e aprovar este artigo.</p>

        <div className="form-group">
          <label>Aprovador</label>
          {loading ? (
            <p>Carregando...</p>
          ) : (
            <select value={selectedAprovador} onChange={(e) => setSelectedAprovador(e.target.value)}>
              {aprovadores.map((aprovador) => (
                <option key={aprovador.id} value={aprovador.id}>
                  {aprovador.nome}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="bc-submit-modal-actions">
          <button onClick={onClose} className="btn-cancelar"><FaUndo /> Cancelar</button>
          <button onClick={handleSubmit} className="btn-salvar" disabled={loading}><FaPaperPlane /> Enviar para Aprovação</button>
        </div>
      </div>
    </div>
  );
};

const NewArticleModal = ({ open, onClose, onConfirm, loading }) => {
  const [titulo, setTitulo] = useState('');
  const [categoria, setCategoria] = useState('Geral');
  const [resumo, setResumo] = useState('');

  useEffect(() => {
    if (open) {
      setTitulo('');
      setCategoria('Geral');
      setResumo('');
    }
  }, [open]);

  if (!open) return null;

  const handleConfirm = () => {
    if (!titulo.trim()) return toast.warn('Informe o título do procedimento.');
    onConfirm({ titulo: titulo.trim(), categoria: categoria.trim() || 'Geral', resumo_curto: resumo.trim() });
  };

  return (
    <div className="bc-modal-overlay">
      <div className="bc-modal-content bc-new-article-modal">
        <button onClick={onClose} className="bc-modal-close-button"><FaTimes /></button>

        <div className="bc-new-article-head">
          <div className="bc-new-article-icon"><FaPlus /></div>
          <div>
            <h3>Novo artigo da base</h3>
            <p>Crie um procedimento com um título claro, categoria e resumo inicial — sem aquela janelinha feia do navegador.</p>
          </div>
        </div>

        <div className="form-group">
          <label>Título do procedimento</label>
          <input
            type="text"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Ex.: KB - Reset de senha com MFA"
            autoFocus
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Categoria</label>
            <input
              type="text"
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              placeholder="Ex.: Acesso, Rede, RH, Financeiro..."
            />
          </div>
          <div className="form-group">
            <label>Resumo curto (opcional)</label>
            <input
              type="text"
              value={resumo}
              onChange={(e) => setResumo(e.target.value)}
              placeholder="Descrição breve para cards e resultados relacionados"
            />
          </div>
        </div>

        <div className="bc-new-article-actions">
          <button onClick={onClose} className="btn-cancelar"><FaUndo /> Cancelar</button>
          <button onClick={handleConfirm} className="btn-salvar" disabled={loading}>
            <FaSave /> {loading ? 'Criando...' : 'Criar artigo'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Barra de reação + copiar ID unificada, sem divisão left/right artificial.
 * Toda a "trilha social" do artigo fica aqui.
 */
const ReactionBar = ({ favorito, feedback, onToggleFavorito, onFeedback, onCopyId, kbId }) => {
  const uteis = feedback?.uteis || 0;
  const naoUteis = feedback?.nao_uteis || 0;
  const minhaReacao = feedback?.minha_reacao || null;

  return (
    <div className="kb-reaction-bar-v2">
      <button
        className={`kb-reaction-pill ${favorito ? 'active favorite' : ''}`}
        onClick={onToggleFavorito}
        type="button"
      >
        {favorito ? <FaStar /> : <FaRegStar />}
        <span className="kb-pill-label">{favorito ? 'Favoritado' : 'Favoritar'}</span>
      </button>

      <button
        className={`kb-reaction-pill ${minhaReacao === 'util' ? 'active success' : ''}`}
        onClick={() => onFeedback('util')}
        type="button"
      >
        <FaThumbsUp />
        <span className="kb-pill-label">Útil</span>
        <span className="kb-pill-count">{uteis}</span>
      </button>

      <button
        className={`kb-reaction-pill ${minhaReacao === 'nao_util' ? 'active danger' : ''}`}
        onClick={() => onFeedback('nao_util')}
        type="button"
      >
        <FaThumbsDown />
        <span className="kb-pill-label">Não útil</span>
        <span className="kb-pill-count">{naoUteis}</span>
      </button>

      <div className="kb-reaction-divider" />

      <button
        className="kb-reaction-pill ghost"
        onClick={() => onCopyId(kbId)}
        type="button"
        title="Copiar ID do artigo"
      >
        <FaCopy />
        <span className="kb-pill-label">Copiar ID</span>
      </button>
    </div>
  );
};

const RelatedArticles = ({ artigos, onOpen }) => (
  <div className="kb-side-card">
    <div className="kb-side-card-title"><FaLink /> Artigos relacionados</div>
    {artigos?.length ? (
      <div className="kb-related-list">
        {artigos.map((item) => (
          <button key={item.id} className="kb-related-item" onClick={() => onOpen(item.id)}>
            <strong>{item.kb_id}</strong>
            <span>{item.titulo}</span>
          </button>
        ))}
      </div>
    ) : (
      <p className="kb-muted">Nenhum relacionado encontrado até o momento.</p>
    )}
  </div>
);

const VersionHistory = ({ versoes }) => (
  <div className="kb-side-card">
    <div className="kb-side-card-title"><FaHistory /> Histórico de versões</div>
    {versoes?.length ? (
      <div className="kb-version-list">
        {versoes.map((versao) => (
          <div key={versao.id || `${versao.numero_versao}-${versao.criado_em}`} className="kb-version-item">
            <div className="kb-version-top">
              <strong>v{versao.numero_versao || '1'}</strong>
              <span>{formatDateTime(versao.criado_em)}</span>
            </div>
            <div className="kb-version-meta">{versao.criado_por_nome || 'Sistema'}</div>
            <p>{versao.resumo_alteracao || 'Atualização registrada sem descrição.'}</p>
          </div>
        ))}
      </div>
    ) : (
      <p className="kb-muted">Sem versões registradas ainda.</p>
    )}
  </div>
);

const ReadingStats = ({ stats }) => (
  <div className="kb-side-card">
    <div className="kb-side-card-title"><FaChartLine /> Estatísticas de leitura</div>
    <div className="kb-mini-stats">
      <div className="kb-mini-stat"><span>Visualizações</span><strong>{stats?.visualizacoes || 0}</strong></div>
      <div className="kb-mini-stat"><span>Leitores únicos</span><strong>{stats?.leitores_unicos || 0}</strong></div>
      <div className="kb-mini-stat"><span>Favoritos</span><strong>{stats?.favoritos || 0}</strong></div>
    </div>
    <div className="kb-stat-footer">Última leitura: <strong>{formatDateTime(stats?.ultima_leitura_em)}</strong></div>
  </div>
);

const CommentsSection = ({ comentarios, novoComentario, setNovoComentario, onSubmit }) => (
  <div className="kb-comments-card">
    <div className="kb-side-card-title"><FaCommentDots /> Comentários do artigo</div>

    <div className="kb-comment-compose">
      <textarea
        value={novoComentario}
        onChange={(e) => setNovoComentario(e.target.value)}
        placeholder="Escreva um comentário, dica, melhoria ou observação..."
        rows={4}
      />
      <div className="kb-comment-actions">
        <button className="btn-salvar" onClick={onSubmit}>
          <FaCommentDots /> Publicar comentário
        </button>
      </div>
    </div>

    {comentarios?.length ? (
      <div className="kb-comments-list">
        {comentarios.map((comentario) => (
          <div key={comentario.id || `${comentario.autor_nome}-${comentario.criado_em}`} className="kb-comment-item">
            <div className="kb-comment-head">
              <strong>{comentario.autor_nome || 'Usuário'}</strong>
              <span>{formatDateTime(comentario.criado_em)}</span>
            </div>
            <p>{comentario.comentario}</p>
          </div>
        ))}
      </div>
    ) : (
      <p className="kb-muted">Ainda não há comentários neste artigo.</p>
    )}
  </div>
);

/**
 * Barra compacta com meta do artigo (categoria / tags / visibilidade).
 * Substitui os 3 cards grandes e vazios — mais denso e legível.
 */
const MetaChipsBar = ({ artigo }) => {
  const tags = Array.isArray(artigo?.tags) ? artigo.tags : [];
  const visibilidade = Array.isArray(artigo?.visibilidade) ? artigo.visibilidade : [];

  return (
    <div className="kb-meta-chips">
      <div className="kb-meta-chip">
        <FaLayerGroup />
        <span className="kb-meta-chip-label">Categoria</span>
        <strong>{artigo?.categoria || 'Não informada'}</strong>
      </div>

      <div className="kb-meta-chip">
        <FaTag />
        <span className="kb-meta-chip-label">Tags</span>
        <strong>{tags.length > 0 ? tags.join(', ') : 'Nenhuma'}</strong>
      </div>

      <div className="kb-meta-chip">
        <FaEye />
        <span className="kb-meta-chip-label">Visibilidade</span>
        <strong>{visibilidade.length > 0 ? visibilidade.join(', ') : 'Todos'}</strong>
      </div>
    </div>
  );
};

export default function BaseConhecimento() {
  const { user } = useOutletContext();
  const { refreshData } = useDataRefresher();
  const fileInputRef = useRef(null);
  const leituraRegistradaRef = useRef(new Set());

  const [artigos, setArtigos] = useState([]);
  const [artigoAtivo, setArtigoAtivo] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [creatingArticle, setCreatingArticle] = useState(false);
  const [loadingExtras, setLoadingExtras] = useState(false);

  const [termoBusca, setTermoBusca] = useState('');
  const [abaAtiva, setAbaAtiva] = useState('Aprovado');
  const [artigosSelecionados, setArtigosSelecionados] = useState([]);
  const [categoriaFiltro, setCategoriaFiltro] = useState('todas');
  const [anexoParaVisualizar, setAnexoParaVisualizar] = useState(null);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isNewArticleModalOpen, setIsNewArticleModalOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const [comentarios, setComentarios] = useState([]);
  const [novoComentario, setNovoComentario] = useState('');
  const [feedbackState, setFeedbackState] = useState({ uteis: 0, nao_uteis: 0, minha_reacao: null });
  const [favorito, setFavorito] = useState(false);
  const [versoes, setVersoes] = useState([]);
  const [relacionados, setRelacionados] = useState([]);
  const [readingStats, setReadingStats] = useState({
    visualizacoes: 0,
    leitores_unicos: 0,
    favoritos: 0,
    ultima_leitura_em: null,
  });

  const PERFIS_DISPONIVEIS = ['operacional', 'financeiro', 'rh', 'admin'];
  const STATUS_DISPONIVEIS = ['Rascunho', 'Pendente', 'Aprovado', 'Rejeitado'];
  const ABAS_DISPONIVEIS = ['Aprovado', 'Pendente', 'Rascunho', 'Rejeitado'];

  const editor = useEditor({
    extensions: [StarterKit, LinkExtension.configure({ openOnClick: false })],
    content: '',
    onUpdate: ({ editor }) => setEditData((prev) => ({ ...prev, conteudo: editor.getHTML() })),
  });

  const perfilUsuario = useMemo(() => normalizePerfil(user?.perfil), [user]);
  const canManage = useMemo(() => ['admin_geral', 'admin'].includes(perfilUsuario), [perfilUsuario]);
  const canCreateOrEditKnowledge = useMemo(() => ['admin_geral', 'admin', 'conhecimento_manager'].includes(perfilUsuario), [perfilUsuario]);

  const canEdit = useCallback((artigo) => {
    if (!user || !perfilUsuario || !artigo) return false;

    if (canCreateOrEditKnowledge) {
      if (artigo.status === 'Pendente' && artigo.aprovador_id !== user.id) return false;
      return true;
    }

    return artigo.criado_por_id === user.id && ['Rascunho', 'Rejeitado'].includes(artigo.status);
  }, [canCreateOrEditKnowledge, perfilUsuario, user]);

  const fetchArtigos = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/conhecimento');
      setArtigos(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Falha ao carregar artigos.');
      console.error('Erro ao carregar artigos:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchArtigos(); }, [fetchArtigos]);

  // ─────────────────────────────────────────────────────────────────────────
  // CARREGA O CONTEÚDO NO EDITOR APENAS AO ENTRAR EM EDIÇÃO OU TROCAR DE ARTIGO.
  //
  // IMPORTANTE: este efeito NÃO pode depender de `editData`. O `onUpdate` do
  // editor recria `editData` a cada tecla; se `editData` estivesse nas deps,
  // o `setContent` rodaria a cada digitação, re-parseando o HTML, colapsando
  // os espaços (whitespace collapsing) e resetando o cursor — exatamente o bug
  // de "texto colado / espaço não funciona".
  //
  // Usamos `artigoAtivo?.id` como gatilho estável (a fonte original do conteúdo)
  // e passamos `false` em `emitUpdate` para não disparar o `onUpdate` de volta.
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isEditing && editor && artigoAtivo) {
      editor.commands.setContent(artigoAtivo.conteudo || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, artigoAtivo?.id, editor]);

  const estatisticas = useMemo(() => ({
    total: artigos.length,
    aprovados: artigos.filter((a) => a.status === 'Aprovado').length,
    pendentes: artigos.filter((a) => a.status === 'Pendente').length,
    rascunhos: artigos.filter((a) => a.status === 'Rascunho').length,
  }), [artigos]);

  const categoriasDisponiveis = useMemo(
    () => [...new Set((artigos || []).map((a) => a.categoria).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [artigos]
  );

  const artigosFiltrados = useMemo(() => {
    const artigosDaAba = canManage ? artigos.filter((a) => a.status === abaAtiva) : artigos;
    const busca = termoBusca.toLowerCase().trim();

    return artigosDaAba.filter((a) => {
      const matchCategoria = categoriaFiltro === 'todas' ? true : a.categoria === categoriaFiltro;
      const matchBusca =
        !busca ||
        a.titulo?.toLowerCase().includes(busca) ||
        a.categoria?.toLowerCase().includes(busca) ||
        a.kb_id?.toLowerCase().includes(busca) ||
        a.criado_por_nome?.toLowerCase().includes(busca);

      return matchCategoria && matchBusca;
    });
  }, [artigos, canManage, abaAtiva, termoBusca, categoriaFiltro]);

  const artigosAgrupados = useMemo(
    () => artigosFiltrados.reduce((acc, artigo) => {
      const categoria = artigo.categoria || 'Sem categoria';
      if (!acc[categoria]) acc[categoria] = [];
      acc[categoria].push(artigo);
      return acc;
    }, {}),
    [artigosFiltrados]
  );

  const loadExtras = useCallback(async (artigoDetalhe) => {
    if (!artigoDetalhe?.id) return;
    setLoadingExtras(true);

    const relatedFallback = buildFallbackRelated(artigoDetalhe, artigos);

    const [comentariosRes, feedbackRes, favoritoRes, versoesRes, relacionadosRes, statsRes] =
      await Promise.allSettled([
        api.get(`/api/conhecimento/${artigoDetalhe.id}/comentarios`),
        api.get(`/api/conhecimento/${artigoDetalhe.id}/feedback`),
        api.get(`/api/conhecimento/${artigoDetalhe.id}/favorito`),
        api.get(`/api/conhecimento/${artigoDetalhe.id}/versoes`),
        api.get(`/api/conhecimento/${artigoDetalhe.id}/relacionados`),
        api.get(`/api/conhecimento/${artigoDetalhe.id}/leituras/estatisticas`),
      ]);

    setComentarios(comentariosRes.status === 'fulfilled' ? safeArray(comentariosRes.value.data) : []);
    setFeedbackState(
      feedbackRes.status === 'fulfilled'
        ? {
            uteis: feedbackRes.value.data?.uteis || 0,
            nao_uteis: feedbackRes.value.data?.nao_uteis || 0,
            minha_reacao: feedbackRes.value.data?.minha_reacao || null,
          }
        : { uteis: 0, nao_uteis: 0, minha_reacao: null }
    );
    setFavorito(favoritoRes.status === 'fulfilled' ? !!favoritoRes.value.data?.favorito : false);
    setVersoes(versoesRes.status === 'fulfilled' ? safeArray(versoesRes.value.data) : []);
    setRelacionados(
      relacionadosRes.status === 'fulfilled' && safeArray(relacionadosRes.value.data).length
        ? relacionadosRes.value.data
        : relatedFallback
    );
    setReadingStats(
      statsRes.status === 'fulfilled'
        ? {
            visualizacoes: statsRes.value.data?.visualizacoes || 0,
            leitores_unicos: statsRes.value.data?.leitores_unicos || 0,
            favoritos: statsRes.value.data?.favoritos || 0,
            ultima_leitura_em: statsRes.value.data?.ultima_leitura_em || null,
          }
        : { visualizacoes: 0, leitores_unicos: 0, favoritos: 0, ultima_leitura_em: null }
    );

    setLoadingExtras(false);
  }, [artigos]);

  const registrarLeitura = useCallback(async (artigoId) => {
    if (!artigoId || leituraRegistradaRef.current.has(artigoId)) return;
    leituraRegistradaRef.current.add(artigoId);

    try {
      await api.post(`/api/conhecimento/${artigoId}/leituras/registrar`);
    } catch (error) {
      console.debug('Leitura ainda não persistida no backend.', error?.response?.status);
    }
  }, []);

  const handleSelectArtigo = async (id) => {
    setIsEditing(false);
    setArtigoAtivo(null);
    setComentarios([]);
    setNovoComentario('');
    setVersoes([]);
    setRelacionados([]);
    setFavorito(false);
    setFeedbackState({ uteis: 0, nao_uteis: 0, minha_reacao: null });
    setReadingStats({ visualizacoes: 0, leitores_unicos: 0, favoritos: 0, ultima_leitura_em: null });

    try {
      const { data } = await api.get(`/api/conhecimento/${id}`);
      setArtigoAtivo(data);
      loadExtras(data);
      registrarLeitura(data.id);
    } catch (error) {
      toast.error('Não foi possível carregar o artigo selecionado.');
      console.error('Erro ao selecionar artigo:', error);
    }
  };

  const handleCreateArtigo = async ({ titulo, categoria, resumo_curto }) => {
    setCreatingArticle(true);

    try {
      const { data: novoArtigo } = await api.post('/api/conhecimento', { titulo, categoria, resumo_curto });

      await fetchArtigos();
      setIsNewArticleModalOpen(false);
      setArtigoAtivo(novoArtigo);

      setEditData({
        ...novoArtigo,
        resumo_curto: novoArtigo.resumo_curto || '',
        banner_url: novoArtigo.banner_url || '',
        tags: Array.isArray(novoArtigo.tags) ? novoArtigo.tags : [],
        visibilidade: Array.isArray(novoArtigo.visibilidade) ? novoArtigo.visibilidade : [],
      });

      setIsEditing(true);
      toast.success('Rascunho criado com sucesso.');
    } catch (error) {
      toast.error('Falha ao criar rascunho do artigo.');
      console.error(error);
    } finally {
      setCreatingArticle(false);
    }
  };

  const handleEdit = () => {
    if (!artigoAtivo) return;

    setEditData({
      ...artigoAtivo,
      resumo_curto: artigoAtivo.resumo_curto || '',
      banner_url: artigoAtivo.banner_url || '',
      tags: Array.isArray(artigoAtivo.tags) ? artigoAtivo.tags : [],
      visibilidade: Array.isArray(artigoAtivo.visibilidade) ? artigoAtivo.visibilidade : [],
    });

    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!editData?.titulo?.trim()) return toast.warn('Informe um título para o artigo.');

    setIsSaving(true);

    try {
      const payload = {
        ...editData,
        tags: JSON.stringify((editData.tags || []).filter(Boolean)),
        visibilidade: JSON.stringify((editData.visibilidade || []).filter(Boolean)),
      };

      await api.put(`/api/conhecimento/${artigoAtivo.id}`, payload);
      toast.success('Artigo salvo com sucesso!');
      await fetchArtigos();
      await handleSelectArtigo(artigoAtivo.id);
    } catch (error) {
      toast.error('Falha ao salvar o artigo.');
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitForApproval = async (aprovador_id) => {
    try {
      await api.put(`/api/conhecimento/${artigoAtivo.id}/submeter`, { aprovador_id });
      toast.success('Artigo enviado para aprovação!');
      setIsSubmitModalOpen(false);
      await handleSelectArtigo(artigoAtivo.id);
      await fetchArtigos();
      refreshData();
    } catch (error) {
      toast.error('Falha ao submeter artigo.');
      console.error(error);
    }
  };

  const handleDecision = async (decisao) => {
    let motivo = '';

    if (decisao === 'rejeitado') {
      motivo = prompt('Por favor, informe o motivo da rejeição:');
      if (motivo === null || motivo.trim() === '') {
        return toast.warn('A rejeição foi cancelada, pois o motivo é obrigatório.');
      }
    }

    try {
      const artigoId = artigoAtivo.id;
      const targetTab = decisao === 'aprovado' ? 'Aprovado' : 'Rejeitado';

      await api.put(`/api/notificacoes/kb/${artigoId}/decisao`, { decisao, motivo });

      setArtigos((prev) =>
        prev.map((artigo) =>
          artigo.id === artigoId
            ? { ...artigo, status: targetTab }
            : artigo
        )
      );

      setAbaAtiva(targetTab);
      setArtigosSelecionados([]);

      toast.success(`Artigo ${decisao} com sucesso!`);

      await fetchArtigos();
      await handleSelectArtigo(artigoId);

      refreshData();
    } catch (error) {
      toast.error('Falha ao processar a decisão.');
      console.error(error);
    }
  };

  const handleDelete = async (artigoId, artigoKbId) => {
    if (!window.confirm(`Tem certeza que deseja excluir permanentemente o artigo ${artigoKbId}? Esta ação não pode ser desfeita.`)) return;

    try {
      await api.delete(`/api/conhecimento/${artigoId}`);
      toast.success('Artigo excluído com sucesso!');
      setArtigoAtivo(null);
      setArtigosSelecionados((prev) => prev.filter((id) => id !== artigoId));
      await fetchArtigos();
      refreshData();
    } catch (error) {
      toast.error('Falha ao excluir o artigo.');
      console.error('Erro ao deletar artigo:', error);
    }
  };

  const uploadFiles = async (filesList) => {
    if (!artigoAtivo?.id) return toast.warn('Salve e selecione um artigo antes de anexar arquivos.');
    if (!filesList || !filesList.length) return;

    const formData = new FormData();
    Array.from(filesList).forEach((file) => formData.append('anexos', file));

    setUploadingFiles(true);

    try {
      await api.post(`/api/conhecimento/${artigoAtivo.id}/anexos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Arquivo(s) enviado(s) com sucesso para o Google Drive.');
      await handleSelectArtigo(artigoAtivo.id);
      await fetchArtigos();
    } catch (error) {
      toast.error('Erro no upload do anexo.');
      console.error(error);
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleFileChange = async (event) => {
    const files = event.target.files;
    if (!files?.length) return;

    await uploadFiles(files);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (anexoId, nomeOriginal) => {
    try {
      const response = await api.get(`/api/anexos/${anexoId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', nomeOriginal);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro no download do anexo:', error);
      toast.error('Não foi possível baixar o anexo.');
    }
  };

  const handleToggleSelecao = (artigoId) => {
    setArtigosSelecionados((prev) =>
      prev.includes(artigoId)
        ? prev.filter((id) => id !== artigoId)
        : [...prev, artigoId]
    );
  };

  const handleBulkAction = async (action) => {
    const total = artigosSelecionados.length;
    if (total === 0) return;

    const actionText = action === 'aprovar' ? 'aprovar' : 'excluir';

    if (!window.confirm(`Você tem certeza que deseja ${actionText} ${total} artigo(s) selecionado(s)?`)) {
      return;
    }

    try {
      await api.post('/api/conhecimento/bulk-actions', {
        artigoIds: artigosSelecionados,
        action,
      });

      toast.success(`${total} artigo(s) foram processados com sucesso!`);
      setArtigosSelecionados([]);
      setArtigoAtivo(null);

      if (action === 'aprovar') {
        setAbaAtiva('Aprovado');
      }

      await fetchArtigos();
      refreshData();
    } catch (error) {
      toast.error('Ocorreu um erro ao processar a ação em massa.');
      console.error('Erro em ação em massa:', error);
    }
  };

  const handleToggleFavorito = async () => {
    if (!artigoAtivo?.id) return;

    const previous = favorito;
    setFavorito(!previous);
    setReadingStats((prev) => ({
      ...prev,
      favoritos: Math.max(0, (prev?.favoritos || 0) + (!previous ? 1 : -1)),
    }));

    try {
      const { data } = await api.post(`/api/conhecimento/${artigoAtivo.id}/favorito`, {
        favorito: !previous,
      });

      setFavorito(!!data?.favorito);
      if (typeof data?.favoritos === 'number') {
        setReadingStats((prev) => ({ ...prev, favoritos: data.favoritos }));
      }
    } catch (error) {
      console.debug('Favorito ainda sem persistência definitiva, usando fallback visual.', error?.response?.status);
    }
  };

  const handleFeedback = async (tipo) => {
    if (!artigoAtivo?.id) return;

    const anterior = feedbackState?.minha_reacao;
    let nextState = { ...feedbackState };

    if (anterior === tipo) {
      nextState.minha_reacao = null;
      if (tipo === 'util') nextState.uteis = Math.max(0, nextState.uteis - 1);
      if (tipo === 'nao_util') nextState.nao_uteis = Math.max(0, nextState.nao_uteis - 1);
    } else {
      nextState.minha_reacao = tipo;

      if (tipo === 'util') {
        nextState.uteis += 1;
        if (anterior === 'nao_util') nextState.nao_uteis = Math.max(0, nextState.nao_uteis - 1);
      }

      if (tipo === 'nao_util') {
        nextState.nao_uteis += 1;
        if (anterior === 'util') nextState.uteis = Math.max(0, nextState.uteis - 1);
      }
    }

    setFeedbackState(nextState);

    try {
      const { data } = await api.post(`/api/conhecimento/${artigoAtivo.id}/feedback`, { tipo });
      if (data) {
        setFeedbackState({
          uteis: data.uteis || 0,
          nao_uteis: data.nao_uteis || 0,
          minha_reacao: data.minha_reacao || null,
        });
      }
    } catch (error) {
      console.debug('Feedback em fallback visual.', error?.response?.status);
    }
  };

  const handleSubmitComment = async () => {
    if (!artigoAtivo?.id || !novoComentario.trim()) {
      return toast.warn('Escreva um comentário antes de publicar.');
    }

    const draft = {
      id: `tmp-${Date.now()}`,
      comentario: novoComentario.trim(),
      autor_nome: user?.nome || 'Você',
      criado_em: new Date().toISOString(),
    };

    setComentarios((prev) => [draft, ...prev]);
    setNovoComentario('');

    try {
      const { data } = await api.post(`/api/conhecimento/${artigoAtivo.id}/comentarios`, {
        comentario: draft.comentario,
      });

      if (data?.comentarios) {
        setComentarios(data.comentarios);
      } else {
        const refresh = await api.get(`/api/conhecimento/${artigoAtivo.id}/comentarios`);
        setComentarios(refresh.data || []);
      }
    } catch (error) {
      console.debug('Comentário adicionado em modo de preview local.', error?.response?.status);
    }
  };

  const onDropZoneClick = () => {
    if (!artigoAtivo?.id) return toast.warn('Crie ou selecione um artigo antes de anexar arquivos.');
    fileInputRef.current?.click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer?.files;
    if (!files?.length) return;

    await uploadFiles(files);
  };

  const imageAttachments = useMemo(
    () => safeArray(artigoAtivo?.anexos).filter((anexo) => getTipoAnexo(anexo?.mimetype, anexo?.nome_original) === 'imagem'),
    [artigoAtivo]
  );

  const renderAnexosManager = () => {
    if (!artigoAtivo) return null;

    return (
      <div className="form-group">
        <label>Anexos (documentos e vídeos)</label>

        <div
          className={`upload-dropzone ${dragActive ? 'drag-active' : ''}`}
          onClick={onDropZoneClick}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="upload-dropzone-icon">
            <FaCloudUploadAlt />
          </div>

          <div className="upload-dropzone-text">
            <strong>Arraste arquivos aqui ou clique para selecionar</strong>
            <span>Suporta vídeo, PDF, DOC, DOCX, imagens e outros arquivos.</span>
            <small>
              Os arquivos serão enviados para uma pasta no Google Drive no padrão:
              <strong> {user?.nome || 'AUTOR'} - {artigoAtivo.kb_id}</strong>
            </small>
          </div>

          {uploadingFiles && <div className="uploading-pill">Enviando...</div>}
        </div>

        <input
          type="file"
          multiple
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          accept="video/*,.pdf,.doc,.docx,image/*,.xls,.xlsx,.ppt,.pptx,.txt"
        />

        <div className="anexos-list">
          {artigoAtivo.anexos?.length > 0 ? (
            artigoAtivo.anexos.map((anexo) => (
              <div key={anexo.id} className="anexo-inline-item">
                <div className="anexo-inline-left">
                  {getAnexoIcon(anexo)}
                  <span>{anexo.nome_original}</span>
                </div>

                <div className="anexo-inline-right">
                  <button
                    type="button"
                    className="mini-action-button"
                    onClick={() => setAnexoParaVisualizar(anexo)}
                  >
                    Visualizar
                  </button>
                  <button
                    type="button"
                    className="mini-action-button"
                    onClick={() => handleDownload(anexo.id, anexo.nome_original)}
                  >
                    Baixar
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="anexo-inline-empty">
              Ainda não há anexos vinculados a este artigo.
            </div>
          )}
        </div>
      </div>
    );
  };

  const isApprovador = user?.id === artigoAtivo?.aprovador_id && artigoAtivo?.status === 'Pendente';
  const podeEditarAtivo = artigoAtivo && canEdit(artigoAtivo);
  const podeSubmeter = podeEditarAtivo && ['Rascunho', 'Rejeitado'].includes(artigoAtivo?.status);

  return (
    <div className="base-conhecimento-container">
      <aside className="bc-sidebar">
        <div className="bc-sidebar-header">
          <div>
            <h3>Base de Conhecimento</h3>
            <span className="bc-sidebar-subtitle">Busca rápida, curadoria e anexos no Drive</span>
          </div>

          {canCreateOrEditKnowledge && (
            <button className="btn-novo-artigo" onClick={() => setIsNewArticleModalOpen(true)}>
              <FaPlus /> Novo Artigo
            </button>
          )}
        </div>

        {canManage && (
          <div className="bc-tabs-container">
            {ABAS_DISPONIVEIS.map((aba) => (
              <div
                key={aba}
                className={`bc-tab ${abaAtiva === aba ? 'active' : ''}`}
                onClick={() => {
                  setAbaAtiva(aba);
                  setArtigoAtivo(null);
                  setArtigosSelecionados([]);
                }}
              >
                {aba}
              </div>
            ))}
          </div>
        )}

        {canManage && artigosSelecionados.length > 0 && (
          <div className="bc-bulk-actions-bar">
            <span>{artigosSelecionados.length} selecionado(s)</span>
            {['Pendente', 'Rascunho', 'Rejeitado'].includes(abaAtiva) && (
              <button className="btn-aprovar-massa" onClick={() => handleBulkAction('aprovar')}>
                <FaCheckCircle /> Aprovar
              </button>
            )}
            <button className="btn-deletar-massa" onClick={() => handleBulkAction('deletar')}>
              <FaTrash /> Excluir
            </button>
            <button className="btn-cancelar-selecao" onClick={() => setArtigosSelecionados([])}>
              <FaTimesCircle /> Limpar
            </button>
          </div>
        )}

        <div className="bc-search-bar">
          <FaSearch />
          <input
            type="text"
            placeholder="Buscar por título, categoria, autor ou KB..."
            value={termoBusca}
            onChange={(e) => setTermoBusca(e.target.value)}
          />
        </div>

        <div className="bc-category-filter">
          <div className="bc-category-filter-header"><FaFilter /><span>Filtros rápidos</span></div>
          <div className="bc-category-chips">
            <button
              className={`bc-chip ${categoriaFiltro === 'todas' ? 'active' : ''}`}
              onClick={() => setCategoriaFiltro('todas')}
            >
              Todas
            </button>

            {categoriasDisponiveis.map((categoria) => (
              <button
                key={categoria}
                className={`bc-chip ${categoriaFiltro === categoria ? 'active' : ''}`}
                onClick={() => setCategoriaFiltro(categoria)}
              >
                {categoria}
              </button>
            ))}
          </div>
        </div>

        <div className="bc-artigos-lista">
          {loading ? (
            <p className="bc-loading">Carregando...</p>
          ) : Object.keys(artigosAgrupados).length === 0 ? (
            <div className="bc-empty-sidebar">
              <FaBook />
              <p>Nenhum artigo encontrado com os filtros atuais.</p>
            </div>
          ) : (
            Object.keys(artigosAgrupados).sort().map((categoria) => (
              <div key={categoria} className="categoria-grupo">
                <h4>{categoria}</h4>
                <ul>
                  {artigosAgrupados[categoria].map((artigo) => (
                    <li key={artigo.id} className={artigoAtivo?.id === artigo.id ? 'active' : ''}>
                      <div className="artigo-lista-item">
                        {canManage && (
                          <input
                            type="checkbox"
                            checked={artigosSelecionados.includes(artigo.id)}
                            onChange={() => handleToggleSelecao(artigo.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}

                        <div className="artigo-lista-content" onClick={() => handleSelectArtigo(artigo.id)}>
                          <div className="artigo-lista-texts">
                            <span className="artigo-lista-titulo">{artigo.kb_id} - {artigo.titulo}</span>
                            <span className="artigo-lista-meta">
                              {artigo.criado_por_nome || 'Sem autor'} • {artigo.anexo_count || 0} anexo(s)
                            </span>
                          </div>

                          <span className={`status-badge-lista status-${artigo.status.toLowerCase().trim()}`}>
                            {artigo.status}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </aside>

      <main className="bc-main-content">
        {!artigoAtivo && (
          <div className="bc-dashboard">
            <div className="bc-hero-card">
              <div className="bc-hero-left">
                <div className="bc-hero-kicker"><FaBook /><span>Portal de conhecimento</span></div>
                <h1>Base de Conhecimento Interativa</h1>
                <p>
                  Organize conteúdos, publique procedimentos, aprove artigos, envie documentos e vídeos
                  para o Google Drive e mantenha tudo centralizado com governança.
                </p>

                <div className="bc-hero-tags">
                  <span><FaLock /> Aprovação</span>
                  <span><FaFolderOpen /> Drive</span>
                  <span><FaVideo /> Vídeos</span>
                  <span><FaUserShield /> RH</span>
                </div>
              </div>

              <div className="bc-stats-grid">
                <div className="bc-stat-card"><span className="bc-stat-label">Total</span><strong>{estatisticas.total}</strong></div>
                <div className="bc-stat-card approved"><span className="bc-stat-label">Aprovados</span><strong>{estatisticas.aprovados}</strong></div>
                <div className="bc-stat-card pending"><span className="bc-stat-label">Pendentes</span><strong>{estatisticas.pendentes}</strong></div>
                <div className="bc-stat-card draft"><span className="bc-stat-label">Rascunhos</span><strong>{estatisticas.rascunhos}</strong></div>
              </div>
            </div>

            <div className="placeholder-view fancy">
              <FaLayerGroup />
              <h2>Selecione um artigo na lateral</h2>
              <p>
                Aqui você poderá visualizar o conteúdo, anexos, vídeos, metadados, comentários,
                favoritos, reações, versões e indicadores de leitura.
              </p>
            </div>
          </div>
        )}

        {artigoAtivo && !isEditing && (
          <div className="artigo-view-refatorado">
            {/* ------ STATUS BAR ------ */}
            <div className={`status-bar-container status-bar-${artigoAtivo.status?.toLowerCase()}`}>
              <div className="status-bar-left">
                <span>Status: <strong>{artigoAtivo.status}</strong></span>
                {artigoAtivo.drive_folder_name && (
                  <span className="drive-folder-pill">
                    <FaFolderOpen /> {artigoAtivo.drive_folder_name}
                  </span>
                )}
              </div>

              {/* Ações de aprovação ficam na status bar, pois são status-dependent */}
              {isApprovador && (
                <div className="status-bar-right">
                  <div className="approval-actions">
                    <button onClick={() => handleDecision('rejeitado')} className="action-reject">
                      <FaThumbsDown /> Rejeitar
                    </button>
                    <button onClick={() => handleDecision('aprovado')} className="action-approve">
                      <FaThumbsUp /> Aprovar
                    </button>
                  </div>
                </div>
              )}
            </div>

            {artigoAtivo.status === 'Rejeitado' && artigoAtivo.rejeicao_motivo && (
              <div className="rejection-reason">
                <strong>Motivo da Rejeição:</strong> {artigoAtivo.rejeicao_motivo}
              </div>
            )}

            {/* ------ HEADER DO ARTIGO COM HIERARQUIA REFINADA ------ */}
            <div className="view-header-v2">
              <div className="view-header-main">
                <span className="kb-id">{artigoAtivo.kb_id}</span>
                <div className="view-header-texts">
                  <h1>{artigoAtivo.titulo}</h1>
                  <div className="view-header-subline">
                    <span><FaClock /> Atualizado por {artigoAtivo.atualizado_por_nome || '—'}</span>
                    <span><FaUserShield /> Autor: {artigoAtivo.criado_por_nome || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Barra de ações primárias e secundárias unificada */}
              {(podeEditarAtivo || podeSubmeter) && (
                <div className="view-header-toolbar">
                  {podeSubmeter && (
                    <button onClick={() => setIsSubmitModalOpen(true)} className="kb-btn kb-btn-primary">
                      <FaPaperPlane /> Submeter para Aprovação
                    </button>
                  )}
                  {podeEditarAtivo && (
                    <button className="kb-btn kb-btn-secondary" onClick={handleEdit}>
                      <FaEdit /> Editar
                    </button>
                  )}
                  {podeEditarAtivo && (
                    <button className="kb-btn kb-btn-danger-ghost" onClick={() => handleDelete(artigoAtivo.id, artigoAtivo.kb_id)}>
                      <FaTrash /> Deletar
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* ------ REACTION BAR UNIFICADA ------ */}
            <ReactionBar
              favorito={favorito}
              feedback={feedbackState}
              onToggleFavorito={handleToggleFavorito}
              onFeedback={handleFeedback}
              onCopyId={copyToClipboard}
              kbId={artigoAtivo.kb_id}
            />

            {/* ------ BANNER (opcional) ------ */}
            {artigoAtivo.banner_url && (
              <div className="kb-article-banner">
                <img src={artigoAtivo.banner_url} alt={artigoAtivo.titulo} />
              </div>
            )}

            {/* ------ RESUMO ------ */}
            {artigoAtivo.resumo_curto && (
              <div className="kb-summary-callout">
                <div className="kb-summary-title"><FaFire /> Resumo rápido do procedimento</div>
                <p>{artigoAtivo.resumo_curto}</p>
              </div>
            )}

            {/* ------ META COMPACTA ------ */}
            <MetaChipsBar artigo={artigoAtivo} />

            {/* ------ CONTEÚDO ------ */}
            <div
              className="view-conteudo"
              dangerouslySetInnerHTML={{
                __html: artigoAtivo.conteudo || '<p><em>Este artigo ainda não tem conteúdo.</em></p>',
              }}
            />

            {/* ------ ANEXOS ------ */}
            <div className="view-anexos">
              <div className="view-anexos-header">
                <h3><FaPaperclip /> Anexos ({artigoAtivo.anexos?.length || 0})</h3>
                {artigoAtivo.drive_folder_name && (
                  <span className="drive-folder-inline">
                    <FaFolderOpen /> Pasta: {artigoAtivo.drive_folder_name}
                  </span>
                )}
              </div>

              {artigoAtivo.anexos?.length ? (
                <div className="anexos-grid">
                  {artigoAtivo.anexos.map((anexo) => (
                    <div key={anexo.id} className="anexo-card">
                      <div className="anexo-card-icon">{getAnexoIcon(anexo)}</div>
                      <div className="anexo-card-body">
                        <strong title={anexo.nome_original}>{anexo.nome_original}</strong>
                        <span>{formatBytes(anexo.tamanho_bytes)}</span>
                        <small>{anexo.mimetype || 'arquivo'}</small>
                      </div>
                      <div className="anexo-card-actions">
                        <button className="anexo-action primary" onClick={() => setAnexoParaVisualizar(anexo)}>
                          Visualizar
                        </button>
                        <button className="anexo-action" onClick={() => handleDownload(anexo.id, anexo.nome_original)}>
                          Baixar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-anexos">Nenhum anexo cadastrado.</p>
              )}
            </div>

            {podeEditarAtivo && (
              <div className="kb-upload-inline-card">
                <div className="kb-side-card-title">
                  <FaPaperclip /> Gerenciar anexos
                </div>
                {renderAnexosManager()}
              </div>
            )}

            {/* ------ BOTTOM GRID: comentários + stack lateral ------ */}
            <div className="kb-bottom-grid">
              <CommentsSection
                comentarios={comentarios}
                novoComentario={novoComentario}
                setNovoComentario={setNovoComentario}
                onSubmit={handleSubmitComment}
              />

              <div className="kb-side-stack">
                <ReadingStats stats={readingStats} />
                <VersionHistory versoes={versoes} />
                <RelatedArticles artigos={relacionados} onOpen={handleSelectArtigo} />
                {loadingExtras && (
                  <div className="kb-side-card">
                    <p className="kb-muted">Carregando informações complementares...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {artigoAtivo && isEditing && editData && (
          <div className="artigo-edit-form">
            <div className="edit-header">
              <div>
                <h3>Editando: {artigoAtivo.kb_id} - {editData.titulo}</h3>
                <p className="edit-subtitle">
                  Conteúdo com aprovação, visibilidade, capa/banner, comentários, feedbacks e anexos no Google Drive.
                </p>
              </div>

              <div className="edit-actions">
                <button className="btn-cancelar" onClick={() => setIsEditing(false)}>
                  <FaUndo /> Cancelar
                </button>
                <button className="btn-salvar" onClick={handleSave} disabled={isSaving}>
                  <FaSave /> {isSaving ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Título</label>
                <input
                  type="text"
                  value={editData.titulo || ''}
                  onChange={(e) => setEditData({ ...editData, titulo: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Categoria</label>
                <input
                  type="text"
                  value={editData.categoria || ''}
                  onChange={(e) => setEditData({ ...editData, categoria: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Resumo curto</label>
                <input
                  type="text"
                  value={editData.resumo_curto || ''}
                  onChange={(e) => setEditData({ ...editData, resumo_curto: e.target.value })}
                  placeholder="Resumo para cards, busca e bloco de destaque"
                />
              </div>

              <div className="form-group">
                <label>Status do Artigo</label>
                <select
                  value={editData.status || 'Rascunho'}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                >
                  {STATUS_DISPONIVEIS.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>URL da capa/banner</label>
                <input
                  type="text"
                  value={editData.banner_url || ''}
                  onChange={(e) => setEditData({ ...editData, banner_url: e.target.value })}
                  placeholder="Cole a URL da imagem da capa"
                />
              </div>

              <div className="form-group">
                <label>Tags (separadas por vírgula)</label>
                <input
                  type="text"
                  value={(editData.tags || []).join(', ')}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean),
                    })
                  }
                />
              </div>
            </div>

            {editData.banner_url && (
              <div className="kb-banner-editor-preview">
                <div className="kb-banner-editor-head"><FaImage /> Prévia da capa/banner</div>
                <div className="kb-banner-editor-box">
                  <img src={editData.banner_url} alt="Prévia da capa" />
                </div>
              </div>
            )}

            {imageAttachments.length > 0 && (
              <div className="form-group">
                <label>Usar imagem anexada como capa</label>
                <div className="kb-banner-choices">
                  {imageAttachments.map((img) => (
                    <button
                      key={img.id}
                      type="button"
                      className="kb-banner-choice"
                      onClick={() =>
                        setEditData({
                          ...editData,
                          banner_url: img.inline_view_url || img.preview_url || img.drive_web_view_link || '',
                        })
                      }
                    >
                      <FaImage /> {img.nome_original}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Conteúdo</label>
              <EditorToolbar editor={editor} />
              <EditorContent editor={editor} className="tiptap-editor" />
            </div>

            <div className="form-group">
              <label>Visibilidade (deixe sem marcar para todos)</label>
              <div className="checkbox-group">
                {PERFIS_DISPONIVEIS.map((perfil) => (
                  <label key={perfil}>
                    <input
                      type="checkbox"
                      checked={(editData.visibilidade || []).includes(perfil)}
                      onChange={() => {
                        const atual = editData.visibilidade || [];
                        const newVis = atual.includes(perfil)
                          ? atual.filter((p) => p !== perfil)
                          : [...atual, perfil];
                        setEditData({ ...editData, visibilidade: newVis });
                      }}
                    /> {perfil}
                  </label>
                ))}
              </div>
            </div>

            {renderAnexosManager()}
          </div>
        )}
      </main>

      {isSubmitModalOpen && (
        <SubmitModal
          onClose={() => setIsSubmitModalOpen(false)}
          onSubmeter={handleSubmitForApproval}
        />
      )}

      {isNewArticleModalOpen && (
        <NewArticleModal
          open={isNewArticleModalOpen}
          onClose={() => setIsNewArticleModalOpen(false)}
          onConfirm={handleCreateArtigo}
          loading={creatingArticle}
        />
      )}

      {anexoParaVisualizar && (
        <AnexoPreviewModal
          anexo={anexoParaVisualizar}
          onClose={() => setAnexoParaVisualizar(null)}
          onDownload={handleDownload}
        />
      )}
    </div>
  );
}